import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/http/client'
import { queryKeys } from '@/api/query-keys'
import { useSessionStore } from '@/stores/useSessionStore'
import type { ApiResponse } from '@/api/http/types'
import {
  isDevNotificationBackendDisabled,
  isNotificationUnavailableError,
  markDevNotificationBackendUnavailable,
  notificationDevQueryOptions,
} from '../lib/notificationDevBackend'
import type { BellNotification } from '../lib/types'

export interface NotificationListPage {
  notifications: BellNotification[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

const NOTIFICATIONS_PAGE_SIZE = 20

export function flattenNotificationPages(pages: NotificationListPage[]): BellNotification[] {
  const seen = new Set<string>()
  const out: BellNotification[] = []
  for (const p of pages) {
    for (const n of p.notifications) {
      if (!n.id || seen.has(n.id)) continue
      seen.add(n.id)
      out.push(n)
    }
  }
  return out
}

/** Mongoose lean row from notification-service (uses `_id`, may add `id`). */
export interface BellNotificationApiRow {
  _id?: string | { $oid: string }
  id?: string | { $oid: string }
  platformUserId?: string | { $oid: string }
  title?: string
  message?: string
  type?: string
  payloadType?: string | null
  relatedUrl?: string | null
  isRead?: boolean
  createdAt?: string | Date | null
}

function extractMongoString(value: string | { $oid: string } | undefined): string {
  if (value == null) return ''
  if (typeof value === 'string') {
    const t = value.trim()
    return t === 'undefined' ? '' : t
  }
  const oid = value.$oid?.trim()
  return oid && oid !== 'undefined' ? oid : ''
}

/** Resolves notification document id from API row (`id`, `_id`, or extended JSON `$oid`). */
export function extractBellNotificationRowId(row: BellNotificationApiRow): string {
  const fromId = extractMongoString(row.id)
  if (fromId) return fromId
  return extractMongoString(row._id)
}

export function mapBellNotificationFromApi(row: BellNotificationApiRow): BellNotification {
  const id = extractBellNotificationRowId(row)

  let createdAt: string | null = null
  if (row.createdAt != null) {
    createdAt =
      typeof row.createdAt === 'string'
        ? row.createdAt
        : new Date(row.createdAt).toISOString()
  }

  let userId = ''
  if (row.platformUserId != null) {
    userId =
      typeof row.platformUserId === 'string'
        ? row.platformUserId
        : extractMongoString(row.platformUserId)
  }

  const rawMessage = row.message == null ? '' : String(row.message).trim()
  const rawTitle = row.title == null ? '' : String(row.title).trim()
  const message = rawMessage || rawTitle

  return {
    id,
    userId,
    message,
    type: row.type ?? 'INFO',
    payloadType: row.payloadType ?? null,
    relatedUrl: row.relatedUrl ?? null,
    isRead: Boolean(row.isRead),
    createdAt,
  }
}

interface PaginatedEnvelope {
  success: boolean
  data: BellNotificationApiRow[]
  meta: { total: number; page: number; per_page: number; total_pages: number } | null
}

async function fetchNotificationsPage(page: number): Promise<NotificationListPage> {
  // Trailing slash matches FastAPI's canonical route (`GET /`) to avoid a
  // 307 redirect that bypasses the Vite proxy and drops the auth cookie.
  const { data } = await apiClient.get<PaginatedEnvelope>('/api/v1/notifications/', {
    params: { page, pageSize: NOTIFICATIONS_PAGE_SIZE },
  })
  const rows = data.data ?? []
  const total = data.meta?.total ?? 0
  const pageSize = data.meta?.per_page ?? NOTIFICATIONS_PAGE_SIZE
  const currentPage = data.meta?.page ?? page
  const totalPages =
    data.meta?.total_pages ?? (pageSize > 0 ? Math.ceil(total / pageSize) : 0)
  return {
    notifications: rows.map(mapBellNotificationFromApi),
    total,
    page: currentPage,
    pageSize,
    totalPages,
  }
}

const EMPTY_NOTIFICATION_PAGE: NotificationListPage = {
  notifications: [],
  total: 0,
  page: 1,
  pageSize: NOTIFICATIONS_PAGE_SIZE,
  totalPages: 0,
}

/** Latest-first bell notifications with incremental pages (20 per page). */
export function useNotifications() {
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)
  return useInfiniteQuery({
    queryKey: queryKeys.notifications.infinite(),
    enabled: isAuthenticated && notificationDevQueryOptions.enabled(),
    retry: notificationDevQueryOptions.retry,
    refetchOnWindowFocus: notificationDevQueryOptions.refetchOnWindowFocus,
    queryFn: async ({ pageParam }): Promise<NotificationListPage> => {
      if (isDevNotificationBackendDisabled()) {
        return EMPTY_NOTIFICATION_PAGE
      }
      try {
        return await fetchNotificationsPage(pageParam)
      } catch (err) {
        if (isNotificationUnavailableError(err)) {
          markDevNotificationBackendUnavailable(err)
          return EMPTY_NOTIFICATION_PAGE
        }
        throw err
      }
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.notifications.length, 0)
      if (lastPage.total > 0) {
        if (loaded >= lastPage.total) return undefined
        return lastPage.page + 1
      }
      if (lastPage.totalPages <= 0) return undefined
      if (lastPage.page >= lastPage.totalPages) return undefined
      return lastPage.page + 1
    },
    staleTime: 30_000,
  })
}

export function useUnreadCount() {
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)
  return useQuery({
    queryKey: queryKeys.notifications.unreadCount(),
    enabled: isAuthenticated && notificationDevQueryOptions.enabled(),
    retry: notificationDevQueryOptions.retry,
    refetchOnWindowFocus: notificationDevQueryOptions.refetchOnWindowFocus,
    queryFn: async (): Promise<number> => {
      if (isDevNotificationBackendDisabled()) {
        return 0
      }
      try {
        const { data } = await apiClient.get<ApiResponse<{ count: number }>>(
          '/api/v1/notifications/unread-count',
        )
        return data.data?.count ?? 0
      } catch (err) {
        if (isNotificationUnavailableError(err)) {
          markDevNotificationBackendUnavailable(err)
          return 0
        }
        throw err
      }
    },
    staleTime: 30_000,
    // Periodic badge refresh disabled for now (re-enable if we want background polling).
    // refetchInterval: 60_000,
  })
}
